import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Sparkle, Briefcase } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const scoreColor = (n) => (n >= 70 ? '#c8f060' : n >= 45 ? '#f6c453' : '#ff9a76');

/**
 * Compact "Weekly Strategy" card surfaced on the dashboard.
 * Empty state → "Run my first strategy" CTA.
 * Active state → campaign name, growth gap, brand health pill, deep-link to deck.
 */
export default function WeeklyStrategyDashboardCard() {
  const [strategy, setStrategy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/strategy/current`);
        setStrategy(data.strategy || null);
      } catch {}
      setLoading(false);
    })();
  }, []);

  if (loading) return null;

  if (!strategy) {
    return (
      <Link to="/dashboard/strategy" data-testid="dashboard-strategy-empty"
        className="block p-5 rounded-2xl transition hover:opacity-95"
        style={{
          background: 'linear-gradient(135deg, rgba(200,240,96,0.06), rgba(200,240,96,0.02))',
          border: '1px solid rgba(200,240,96,0.18)',
        }}>
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(200,240,96,0.12)' }}>
            <Briefcase size={20} style={{ color: '#c8f060' }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] tracking-[0.2em] uppercase font-bold mb-1" style={{ color: '#c8f060' }}>Weekly Strategy</p>
            <p className="text-base font-semibold mb-1" style={{ color: '#f5f0e8', fontFamily: "'Instrument Serif', serif" }}>
              Your marketing strategist is on staff.
            </p>
            <p className="text-xs" style={{ color: 'rgba(245,240,232,0.5)' }}>
              Brand audit · industry benchmark · campaign deck — every Monday.
            </p>
          </div>
          <div className="flex items-center gap-1 text-[12px] font-semibold shrink-0 self-center" style={{ color: '#c8f060' }}>
            Run now <ArrowRight size={13} />
          </div>
        </div>
      </Link>
    );
  }

  const score = strategy.brand_health_score || 0;
  const color = scoreColor(score);

  return (
    <Link to={`/dashboard/strategy?id=${strategy.id}`} data-testid="dashboard-strategy-card"
      className="block p-5 rounded-2xl transition hover:opacity-95"
      style={{
        background: '#141414',
        border: '1px solid rgba(255,255,255,0.06)',
        borderLeft: '2px solid #c8f060',
      }}>
      <div className="flex items-start gap-4">
        {/* Mini score ring */}
        <div className="relative w-12 h-12 shrink-0">
          <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="3" />
            <circle cx="18" cy="18" r="15.9" fill="none" stroke={color} strokeWidth="3" strokeDasharray={`${score} ${100 - score}`} strokeLinecap="round" />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-xs font-bold tabular-nums" style={{ color }}>{score}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-[10px] tracking-[0.2em] uppercase font-bold" style={{ color: '#c8f060' }}>This week's campaign</p>
            <span className="px-2 py-0.5 rounded-full text-[9px] font-semibold capitalize" style={{ background: 'rgba(200,240,96,0.08)', color: 'rgba(200,240,96,0.7)', border: '1px solid rgba(200,240,96,0.15)' }}>
              {(strategy.campaign_objective || '').replace(/_/g, ' ')}
            </span>
          </div>
          <p className="text-lg font-semibold leading-tight mb-1 truncate" style={{ color: '#f5f0e8', fontFamily: "'Instrument Serif', serif" }}>
            {strategy.campaign_name}
          </p>
          {strategy.growth_gap && (
            <p className="text-xs line-clamp-2" style={{ color: 'rgba(245,240,232,0.5)' }}>{strategy.growth_gap}</p>
          )}
        </div>
        <div className="flex items-center gap-1 text-[12px] font-semibold shrink-0 self-center" style={{ color: '#c8f060' }}>
          <Sparkle size={13} weight="fill" /> View deck <ArrowRight size={13} />
        </div>
      </div>
    </Link>
  );
}
